import { createContext, useContext, type ReactNode } from 'react';
import type { DocumentVersionDetails } from './types';

/**
 * Fetches a presigned URL + metadata for a previously-uploaded document
 * version. Picker widgets call this lazily when they need to render a
 * thumbnail or "Open" link — the upload itself only returns ids.
 *
 * Implementations should wrap `onboardingService.getDocumentVersion` (JWT)
 * or `inviteService.getDocumentVersion` (invite token) and bind
 * `entity_type` + `entity_id` so the picker only has to know the version id.
 */
export type FormDocumentFetcher = (
  versionId: number,
  options?: { signal?: AbortSignal },
) => Promise<DocumentVersionDetails>;

const FormDocumentFetcherContext = createContext<FormDocumentFetcher | null>(null);

export const FormDocumentFetcherProvider = ({
  fetcher,
  children,
}: {
  fetcher: FormDocumentFetcher;
  children: ReactNode;
}) => {
  return (
    <FormDocumentFetcherContext.Provider value={fetcher}>
      {children}
    </FormDocumentFetcherContext.Provider>
  );
};

/**
 * Returns the configured fetcher or `null` when no provider wraps the tree.
 * In that case the picker falls back to filename-only display (no preview).
 */
export const useFormDocumentFetcher = (): FormDocumentFetcher | null => {
  return useContext(FormDocumentFetcherContext);
};
